import { useEffect, useRef, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * Icon-only "Copy VIN" button used on appointment tiles and inside
 * the appointment edit dialog's vehicle summary card. Three states:
 *
 *   - idle     — Copy icon, tooltip on hover/long-press
 *   - copied   — Check icon + brief "Copied" toast for 1.2s
 *   - hovered  — visual handled via CSS hover utilities
 *
 * Touch target is 32×32 on mobile (below the 44×44 minimum used for
 * primary actions, because this is a secondary affordance on a
 * tappable tile — sitting at 44 would crowd the tile body). The icon
 * itself is 14×14 on desktop and 16×16 on mobile per the spec.
 *
 * Stops pointer event propagation so the surrounding tile's drag /
 * click-to-edit handlers don't fire when the user taps the button.
 */
export function CopyVinButton({
  vin,
  className = '',
  variant = 'tile',
}: {
  vin: string
  className?: string
  // 'tile' — small icon-only, used on the calendar tile.
  // 'dialog' — slightly larger, paired with the VIN string in a
  // monospace block inside the edit dialog.
  variant?: 'tile' | 'dialog'
}) {
  const { t } = useTranslation('appointments')
  const [copied, setCopied] = useState(false)
  const timerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (timerRef.current) window.clearTimeout(timerRef.current)
    }
  }, [])

  async function handleCopy(e: React.MouseEvent) {
    e.preventDefault()
    e.stopPropagation()
    try {
      await navigator.clipboard.writeText(vin)
      setCopied(true)
      if (timerRef.current) window.clearTimeout(timerRef.current)
      timerRef.current = window.setTimeout(() => setCopied(false), 1200)
    } catch {
      // Browsers can reject clipboard writes (insecure context, no
      // permission). Fall back to a noop — the VIN is still visible
      // in the dialog and copyable by hand.
    }
  }

  // Sizing differs per variant. Touch targets are larger than icons.
  const sizing =
    variant === 'tile'
      ? 'h-7 w-7 sm:h-6 sm:w-6'
      : 'h-9 w-9 sm:h-8 sm:w-8'
  const iconSize =
    variant === 'tile' ? 'size-4 sm:size-3.5' : 'size-4'

  const label = copied ? t('tile.vin_copied') : t('tile.copy_vin')

  return (
    <>
      <button
        type="button"
        onClick={handleCopy}
        onPointerDown={(e) => e.stopPropagation()}
        aria-label={label}
        title={label}
        className={`inline-flex shrink-0 items-center justify-center rounded-[6px] transition-[background-color,color,transform] [transition-duration:120ms] active:scale-[0.97] motion-reduce:transition-none motion-reduce:active:scale-100 ${sizing} ${
          copied
            ? 'text-foreground'
            : 'text-ink-3 [@media(hover:hover)_and_(pointer:fine)]:hover:bg-muted [@media(hover:hover)_and_(pointer:fine)]:hover:text-foreground'
        } ${className}`}
      >
        {copied ? (
          <Check className={iconSize} aria-hidden />
        ) : (
          <Copy className={iconSize} aria-hidden />
        )}
      </button>
      {copied ? (
        <div
          role="status"
          aria-live="polite"
          className="pointer-events-none fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-full bg-foreground px-4 py-2 text-sm font-[550] text-background shadow-2"
        >
          {t('tile.vin_copied_toast')}
        </div>
      ) : null}
    </>
  )
}
